import { useEffect, useRef } from "react";
import { Button, TextInput } from "@datapay-by-datacom/design-system";
import type { MyPayEnvironment } from "../../generated/core-api";
import { usePayRunLookup } from "../hooks/usePayRunLookup";
import { isPayRunWritable, type BoundPayRun } from "../types/matching";
import s from "./PayRunIdStep.module.css";

export interface PayRunIdStepProps {
  companyCode: string;
  mypayEnvironment: MyPayEnvironment;
  boundPayRun: BoundPayRun | null;
  onBound: (payRun: BoundPayRun) => void;
  onCleared: () => void;
  disabled?: boolean;
}

/** NZ-style display for ISO period end (yyyy-MM-dd → d/M/yyyy). */
function formatPeriodEnd(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
}

/**
 * Option A destination gate — same interaction pattern as CompanyContextStep:
 * enter Pay run ID → Validate → bind. Errors on the field only; success is a
 * single ✓ line (no duplicate banner + field error).
 */
export function PayRunIdStep({
  companyCode,
  mypayEnvironment,
  boundPayRun,
  onBound,
  onCleared,
  disabled = false,
}: PayRunIdStepProps) {
  const lookup = usePayRunLookup(companyCode, mypayEnvironment);
  const onBoundRef = useRef(onBound);
  onBoundRef.current = onBound;

  // Auto-bind on successful validate when the run is writable (mirrors company context).
  useEffect(() => {
    const resolved = lookup.resolvedPayRun;
    if (!resolved || lookup.resolveError) return;
    if (!isPayRunWritable(resolved.state)) return;
    if (boundPayRun?.payRunId === resolved.payRunId) return;
    onBoundRef.current(resolved);
  }, [boundPayRun?.payRunId, lookup.resolveError, lookup.resolvedPayRun]);

  const handleInputChange = (value: string) => {
    if (disabled) return;
    lookup.setPayRunIdInput(value);
    if (boundPayRun || lookup.resolveError || lookup.resolvedPayRun) {
      lookup.clearResolved();
      onCleared();
    }
  };

  const handleValidate = () => {
    if (disabled) return;
    // Clear the prior binding before re-validate so a Finalised/failed
    // lookup cannot leave the old writable run (and upload gate) active.
    lookup.clearResolved();
    onCleared();
    void lookup.validatePayRunId();
  };

  const displayRun = boundPayRun ?? lookup.resolvedPayRun;
  const isBlocked = displayRun !== null && !isPayRunWritable(displayRun.state);
  const fieldError =
    lookup.resolveError ??
    (isBlocked
      ? `Matching is blocked while this pay run is ${displayRun?.state}. Choose a Pending or In Progress run.`
      : undefined);

  const showSuccess =
    displayRun !== null && !lookup.resolveError && isPayRunWritable(displayRun.state);

  return (
    <section className={s.container} aria-labelledby="pay-run-id-heading">
      <h3 id="pay-run-id-heading" className={s.fieldHeading}>
        Pay run ID
      </h3>

      <div className={s.row}>
        <TextInput
          aria-label="Pay run ID"
          value={lookup.payRunIdInput}
          isRequired
          onChange={handleInputChange}
          onInput={(evt) => handleInputChange(evt.currentTarget.value)}
          onKeyDown={(evt) => {
            if (
              evt.key === "Enter" &&
              !disabled &&
              !lookup.isResolving &&
              lookup.payRunIdInput.trim()
            ) {
              handleValidate();
            }
          }}
          isDisabled={disabled || lookup.isResolving}
          isInvalid={!!fieldError}
          errorMessage={fieldError}
        />
        <Button
          variant="secondary"
          size="large"
          aria-label="Validate pay run"
          onPress={handleValidate}
          isPending={lookup.isResolving}
          isDisabled={
            disabled || lookup.isResolving || !lookup.payRunIdInput.trim()
          }
        >
          Validate
        </Button>
      </div>

      {showSuccess && displayRun && (
        <p className={s.payRunSuccess} role="status">
          <span aria-hidden="true">✓</span>{" "}
          {displayRun.payGroupName} ({displayRun.state}) · period end{" "}
          {formatPeriodEnd(displayRun.periodEnd)}
        </p>
      )}
    </section>
  );
}
